import { phoneDigits } from './phoneFormat'

/**
 * Sign-up's PUT /organization/:orgId/paper (specs/customer-sync, T5/T9):
 * saves the contact details from the sign-up questions on the row that the
 * subscription just made or adopted.
 *
 * How T5 finds the row: the union of the ids sent (rowId, linkToSavedMember,
 * allcalUserId); only when none of them match, the one live row with
 * `email` (case-insensitive). Sign-up never knows the row _id (the subscribe
 * call answers with a plain string), so it sends:
 *   - allcalUserId: the new login; subscribe has just stored it on the row;
 *   - email: the login's email, the same value subscribe stored, as the
 *     fallback (and a no-op as a field);
 *   - linkToSavedMember: the saved member id when sign-up arrived through
 *     /member/:memberId, so the organizer's row is the one found (and two
 *     different rows are refused, not silently split).
 * It never sends isNoPaySelfRegisterOrLogin: sign-up only saves after a
 * successful subscribe, which has made the row, so a missing row is an error
 * to show, not a reason to create another one.
 *
 * Phones go as 10 digits. One the API would refuse (invalid-phone, which
 * fails the whole save) is left out and named in `dropped`.
 *
 * @return {{ body: Object, dropped: string[] }}
 */
export const signupPaperBody = ({ additionalInfo, allcalUserId, email, linkToSavedMember }) => {
    const info = additionalInfo || {}
    const body = {}
    const dropped = []

    if (allcalUserId) body.allcalUserId = String(allcalUserId)
    if (typeof email === 'string' && email.trim()) body.email = email.trim()
    if (linkToSavedMember && linkToSavedMember !== 'undefined') body.linkToSavedMember = String(linkToSavedMember)

    const phone = (key, label) => {
        const raw = info[key]
        if (raw === undefined || raw === null || String(raw).trim() === '') return
        const digits = phoneDigits(raw)
        if (digits.length === 10) body[key] = digits
        else dropped.push(label)
    }
    phone('phoneNumber', 'phone number')
    phone('emergencyContact', 'emergency contact phone')

    ;['address', 'mailingAddress', 'emergencyContactName', 'referral'].forEach((k) => {
        if (typeof info[k] === 'string' && info[k].trim()) body[k] = info[k].trim()
    })
    if (info.dateOfBirth) {
        const d = new Date(info.dateOfBirth)
        if (!isNaN(d.getTime())) body.dateOfBirth = d.toISOString()
    }
    return { body, dropped }
}

// Kept across the reload that ends sign-up, shown once by My Account.
export const SIGNUP_NOTICE_KEY = 'signupDetailsNotice'

/** The member-facing message when sign-up's details were not (all) saved. */
export const signupNoticeMessage = ({ error, dropped }) => {
    if (error) {
        const why = error.message ? ' (' + error.message + ')' : ''
        return 'Your membership is set up, but your contact details could not be saved' + why +
            '. Please add them under Account Details, Edit Details, or contact us.'
    }
    if (dropped && dropped.length) {
        return 'Your membership is set up, but your ' + dropped.join(' and ') +
            ' was not a 10-digit US number and was not saved. Please add it under Account Details, Edit Details.'
    }
    return null
}
